import {
  Box,
  Grid,
  Link,
  Stack,
  Typography,
  Button,
  Tooltip,
} from "@mui/material";

import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { FeedGrid } from "../components/OwnerInfoDetailFeedGrid";
import { KeyboardArrowLeft } from "@mui/icons-material";
import React, { useState } from "react";
import { useApiClient } from "@/hooks/useApiClient";
import { useParams, Link as RouterLink } from "react-router-dom";
import useSWR from "swr";
import { isActionEnabled, statusToLabel } from "@/features/ownerInfo/utils";
import { type RequestDetailAndRealEstates } from "@/features/ownerInfo/types";
import CircularProgress from "@mui/material/CircularProgress";
import { useFeatureFlags } from "@/configs/featureFlag";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { OwnerInfoEditModal } from "@/features/ownerInfo/components/OwnerInfoEditModal";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { z } from "zod";

const ownerAnalyzeDetailContractSchema = z
  .object({
    isOtherUserResource: z.boolean(),
    hasTriggerPictureRequest: z.boolean(),
  })
  .passthrough();

const validateOwnerAnalyzeDetailContract = (
  payload: unknown,
  endpoint: string
): void => {
  const parsed = ownerAnalyzeDetailContractSchema.safeParse(payload);
  if (parsed.success) return;

  const error = new Error(`API contract violation at ${endpoint}`);
  Sentry.captureException(error, {
    extra: {
      endpoint,
      issues: parsed.error.issues,
    },
  });
  throw error;
};

export const OwnerInfoDetail: React.FC = () => {
  const { apiClient } = useApiClient();
  const { id } = useParams();
  const { mypageTop } = useFeatureFlags();
  const identificationNameDisabledMessage =
    "他ユーザーが設定した識別名は編集できません";
  const [openOwnerInfoEditDialog, setOpenOwnerInfoEditDialog] =
    useState<boolean>(false);
  const fetcher = async (url: string): Promise<RequestDetailAndRealEstates> => {
    const { data } = await apiClient.get<RequestDetailAndRealEstates>(url);
    validateOwnerAnalyzeDetailContract(data, url);
    return data;
  };
  const { data, mutate } = useSWR<RequestDetailAndRealEstates>(
    id ? `/owner-analyze/${id}` : null,
    fetcher,
    { revalidateOnFocus: true }
  );
  if (data == null) {
    return <CircularProgress />;
  }

  // 識別名を更新後、すぐにデータの再フェッチを行う
  const handleRefreshAfterEdit = (): void => {
    mutate(data).catch(() => {
      toast.error("データの再取得に失敗しました。");
    });
  };

  return (
    <PagePaper>
      <form>
        <Grid container sx={{ mb: 2 }} rowSpacing={1}>
          <Grid item xs={12} md={8}>
            <Grid container>
              <Grid item xs={12}>
                {/* ページタイトル */}
                <Box sx={{ display: "flex", mb: 3 }}>
                  <LibraryBooksIcon sx={{ mr: 1 }} fontSize="large" />
                  <PageTitle>所有者事項データ化詳細</PageTitle>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid container sx={{ mb: 2 }} rowSpacing={1}>
          <Grid item xs={12} sx={{ textAlign: "left" }}>
            <Grid container rowSpacing={1}>
              <Grid item xs={12}>
                <Stack direction={"row"} spacing={2}>
                  <Typography
                    color={"blue"}
                    sx={{
                      fontSize: "20px",
                      fontWeight: "bold",
                    }}
                  >
                    ステータス: {statusToLabel(data.status)}
                  </Typography>
                  <Typography
                    color={"blue"}
                    sx={{
                      fontSize: "20px",
                      fontWeight: "bold",
                    }}
                  >
                    TOTAL:{" "}
                    <Typography
                      component={"span"}
                      color={"red"}
                      sx={{
                        fontSize: "20px",
                        fontWeight: "bold",
                      }}
                    >
                      {data?.realEstates.length}
                    </Typography>
                    件
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  sx={{ color: "black", fontSize: "20px", fontWeight: "bold" }}
                >
                  識別番号: {data?.taskId}{" "}
                </Typography>
                <Typography
                  sx={{ color: "black", fontSize: "20px", fontWeight: "bold" }}
                >
                  識別名: {data?.identificationName}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sx={{ textAlign: "left" }}>
            <Tooltip
              title={
                data?.isOtherUserResource
                  ? identificationNameDisabledMessage
                  : ""
              }
              placement="top"
              arrow
              disableHoverListener={!data?.isOtherUserResource}
            >
              <span>
                <Button
                  variant="outlined"
                  component={Link}
                  onClick={() => {
                    setOpenOwnerInfoEditDialog(true);
                  }}
                  disabled={data?.isOtherUserResource}
                  sx={{
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  識別名編集
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>

        <SectionPaper>
          <FeedGrid
            row={data?.realEstates}
            requestId={data?.id}
            disabledAction={!isActionEnabled(data?.status)}
            taskId={data?.taskId}
            identificationName={data?.identificationName}
            isPropertyDetailOptionCredit={data?.isPropertyDetailOptionCredit}
            isDmCredit={data?.isDmCredit}
            hasTriggerPictureRequest={data?.hasTriggerPictureRequest}
            succeedCount={data?.succeedCount}
            onDownloadComplete={() => {
              mutate(data).catch(() => {
                toast.error("データの再取得に失敗しました。");
              });
            }}
          />
          <Box sx={{ mb: 6 }} />
          <Stack
            direction={"row"}
            spacing={4}
            justifyContent={"center"}
            mb={2}
          ></Stack>
        </SectionPaper>

        {/* 戻る */}
        <Box sx={{ mt: 2 }}>
          <Link
            component={RouterLink}
            to={mypageTop ? "/mypage" : "/feed"}
            sx={{ display: "inline-block" }}
          >
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>
                {mypageTop ? "マイページに戻る" : "不動産の権利異動検索に戻る"}
              </Typography>
            </Box>
          </Link>
        </Box>
      </form>
      {openOwnerInfoEditDialog && !data?.isOtherUserResource && (
        <OwnerInfoEditModal
          open={openOwnerInfoEditDialog}
          setOpen={setOpenOwnerInfoEditDialog}
          handleClose={handleRefreshAfterEdit}
          pictureRequestId={Number(id)}
          data={data}
        />
      )}
    </PagePaper>
  );
};
